import * as React from 'react';
import { toast } from 'sonner';
import { Upload, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';

/** A single recognized column the import maps into. */
export interface ImportField {
  /** CSV header key (lower-cased) this maps from. */
  key: string;
  /** Human label shown in the preview header. */
  label: string;
  /** When true, a row missing this value will be skipped server-side. */
  required?: boolean;
}

export interface ImportSummary {
  created: number;
  skipped: { row: number; reason: string }[];
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: string;
  /** Columns recognized by this import (drives the template + preview). */
  fields: ImportField[];
  /** Posts the parsed rows; returns the server's {created, skipped} summary. */
  onImport: (rows: Record<string, string>[]) => Promise<ImportSummary>;
  /** Called after a successful import so the parent can refresh its list. */
  onImported: () => void;
}

// ── Tiny dependency-free CSV parser (mirrors api/core/shared/csv.ts) ──────────
function parseCsvCells(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  if (n > 0 && text.charCodeAt(0) === 0xfeff) i = 1; // strip BOM
  let saw = false;
  for (; i < n; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += ch; }
      continue;
    }
    if (ch === '"') { inQuotes = true; saw = true; }
    else if (ch === ',') { row.push(field); field = ''; saw = true; }
    else if (ch === '\r') {
      if (text[i + 1] !== '\n') { row.push(field); rows.push(row); row = []; field = ''; saw = false; }
    } else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; saw = false; }
    else { field += ch; saw = true; }
  }
  if (saw || field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const cells = parseCsvCells(text).filter((r) => r.some((c) => c.trim() !== ''));
  if (cells.length === 0) return { headers: [], rows: [] };
  const headers = cells[0].map((h) => h.trim().toLowerCase());
  const rows: Record<string, string>[] = [];
  for (let r = 1; r < cells.length; r++) {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => { if (h) obj[h] = (cells[r][idx] ?? '').trim(); });
    rows.push(obj);
  }
  return { headers, rows };
}

const PREVIEW_LIMIT = 20;

export function ImportCsvDialog({
  open, onOpenChange, title, description, fields, onImport, onImported,
}: Props) {
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [rows, setRows] = React.useState<Record<string, string>[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [summary, setSummary] = React.useState<ImportSummary | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) {
      setFileName(null); setHeaders([]); setRows([]); setSummary(null); setBusy(false);
    }
  }, [open]);

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseCsv(text);
      if (parsed.rows.length === 0) {
        toast.error('No data rows found', { description: 'The file needs a header row plus at least one data row.' });
        return;
      }
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setSummary(null);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file);
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = ''; // allow re-selecting the same file
  }

  const knownKeys = fields.map((f) => f.key);
  const unknownHeaders = headers.filter((h) => !knownKeys.includes(h));
  const missingRequired = fields
    .filter((f) => f.required)
    .filter((f) => !headers.includes(f.key))
    .map((f) => f.key);

  async function doImport() {
    setBusy(true);
    try {
      const result = await onImport(rows);
      setSummary(result);
      if (result.created > 0) {
        toast.success(`Imported ${result.created} row${result.created === 1 ? '' : 's'}`, {
          description: result.skipped.length ? `${result.skipped.length} skipped` : 'No rows skipped',
        });
        onImported();
      } else {
        toast.error('Nothing imported', { description: `${result.skipped.length} row(s) skipped` });
      }
    } catch (err: any) {
      toast.error('Import failed', { description: err?.message ?? 'Unknown error' });
    } finally { setBusy(false); }
  }

  function downloadTemplate() {
    const headerLine = fields.map((f) => f.key).join(',');
    const blob = new Blob([headerLine + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Step 1 / no file yet */}
        {!fileName && !summary && (
          <div className="space-y-4 pt-2">
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/30 px-6 py-10 text-center">
              <Upload className="mx-auto h-7 w-7 text-muted-foreground" strokeWidth={1.6} />
              <p className="text-[14px] font-semibold text-foreground mt-3">Choose a CSV file</p>
              <p className="text-[12.5px] text-muted-foreground mt-1">
                The first row must be a header. Recognized columns:{' '}
                <span className="font-medium text-foreground">{fields.map((f) => f.key).join(', ')}</span>
              </p>
              <div className="mt-5 flex items-center justify-center gap-2">
                <Button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-full h-9 px-4 text-[13px] font-semibold gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> Select file
                </Button>
                <Button type="button" variant="outline" onClick={downloadTemplate} className="rounded-full h-9 px-4 text-[13px] font-semibold">
                  Download template
                </Button>
              </div>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFileChange} />
            </div>
          </div>
        )}

        {/* Step 2 / preview */}
        {fileName && !summary && (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[13px] text-muted-foreground">
                <span className="font-semibold text-foreground">{fileName}</span> — {rows.length} row{rows.length === 1 ? '' : 's'}
                {rows.length > PREVIEW_LIMIT ? ` (showing first ${PREVIEW_LIMIT})` : ''}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="rounded-full text-[12.5px]">
                Choose different file
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFileChange} />
            </div>

            {missingRequired.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-2 text-[12.5px] text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Missing required column{missingRequired.length > 1 ? 's' : ''}: <strong>{missingRequired.join(', ')}</strong>. Those rows will be skipped.</span>
              </div>
            )}
            {unknownHeaders.length > 0 && (
              <p className="text-[12px] text-muted-foreground">
                Ignored columns: {unknownHeaders.join(', ')}
              </p>
            )}

            <div className="overflow-auto max-h-[320px] rounded-xl ring-1 ring-border/40">
              <table className="w-full text-[12.5px] border-collapse">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    {fields.map((f) => (
                      <th key={f.key} className="text-left font-semibold text-foreground px-3 py-2 whitespace-nowrap">
                        {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, PREVIEW_LIMIT).map((r, i) => (
                    <tr key={i} className="border-t border-border/30">
                      {fields.map((f) => (
                        <td key={f.key} className="px-3 py-1.5 text-foreground/90 whitespace-nowrap max-w-[180px] truncate">
                          {r[f.key] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="button" onClick={doImport} disabled={busy}>
                {busy ? 'Importing…' : `Import ${rows.length} row${rows.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 / summary */}
        {summary && (
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3 rounded-xl bg-success/10 ring-1 ring-success/25 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
              <div>
                <p className="text-[14px] font-semibold text-foreground">
                  {summary.created} row{summary.created === 1 ? '' : 's'} imported
                </p>
                {summary.skipped.length > 0 && (
                  <p className="text-[12.5px] text-muted-foreground">{summary.skipped.length} row(s) skipped</p>
                )}
              </div>
            </div>

            {summary.skipped.length > 0 && (
              <div className="overflow-auto max-h-[240px] rounded-xl ring-1 ring-border/40">
                <table className="w-full text-[12.5px] border-collapse">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left font-semibold text-foreground px-3 py-2 w-16">Row</th>
                      <th className="text-left font-semibold text-foreground px-3 py-2">Reason skipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.skipped.map((s, i) => (
                      <tr key={i} className="border-t border-border/30">
                        <td className="px-3 py-1.5 text-foreground/90 tabular-nums">{s.row}</td>
                        <td className="px-3 py-1.5 text-foreground/90">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
